import React, { useState } from 'react'
import Title from '@components/ui/type/Title'
import Translations from '@helpers/translations'
import { submitOutOfStockForm } from '@api/apiCalls'
import { ProductOption } from '../../../types/product'
import { analytics } from '@helpers/analytics'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import EmailFieldValidation from '@components/ui/EmailFieldValidation'
import { Button, SubmitButton } from '@components/ui/Button'
import trackError from '@helpers/trackError'
import Notification from '@components/ui/Notification'
import BackInStockForm from './BackInStockForm'
import styles from './styles.module.scss'
import { SelectedOptions, AvailableOptions } from './types'

const sendAnalyticsEvents = (
  options: SelectedOptions,
  productType: string,
  productTitle: string
) => {
  analytics('Out of Stock - Completed', {
    label: 'completed',
    name: `${productType} - ${productTitle}`,
    props: options
  })
}

type SelectedProductProperties = {
  id: number
  title: string
  productType: string
  productFeed: {
    product_id?: string
  }
  options: ProductOption[]
}

type BackInStockFormWrapperProps = {
  availableOptions: AvailableOptions
  selectedOptions: SelectedOptions
  selectedProduct: SelectedProductProperties
  handleCloseModal: () => void
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
}

const FormNotification = ({ state }: { state: string }) => {
  switch (state) {
    case 'error':
      return (
        <Notification
          type="error"
          content={Translations.localise('product.oos-modal.error')}
        />
      )
    case 'success':
      return (
        <Notification
          type="success"
          theme="dark"
          content={Translations.localise('product.oos-modal.success')}
        />
      )
    default:
      return null
  }
}

function formatOptions(options: SelectedOptions): SelectedOptions {
  const formattedOptions: SelectedOptions = {}
  Object.entries(options).forEach(([option, value]) => {
    const formattedOption = option
      .replace('taper', 'lowerLeg')
      .replace('swimshortsleg', 'shortsleg')
      .replace('chest size', 'size')
    formattedOptions[formattedOption] = value
  })
  return formattedOptions
}

const BackInStockFormWrapper = ({
  selectedOptions,
  availableOptions,
  selectedProduct,
  handleCloseModal,
  setSelectedOptions
}: BackInStockFormWrapperProps) => {
  const [email, setEmail] = useState('')
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const [showFormState, setShowFormState] = useState('')

  const { productType, productFeed, id, title } = selectedProduct

  const handleSelectingFormOptions = (selectedFormOption: SelectedOptions) => {
    setSelectedOptions(currentSelectedOptions => ({
      ...currentSelectedOptions,
      ...selectedFormOption
    }))
  }

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setButtonState('loading')
    try {
      const formattedOptions = formatOptions(selectedOptions)
      const formPayload = {
        email,
        permanentDomain: /production|staging/.test(process.env.API_ENV)
          ? window.location.origin
          : '/',
        colour: title,
        productType,
        partial_sku: productFeed?.product_id || '',
        product_id: id,
        ...formattedOptions
      }
      await submitOutOfStockForm(formPayload)
      sendAnalyticsEvents(formattedOptions, productType, title)
      setShowFormState('success')
    } catch (error) {
      trackError(new Error('Submit Out Of Stock Failed'))
      setShowFormState('error')
      setButtonState('ready')
    }
  }
  const isSubmitButtonDisabled =
    Object.keys(selectedOptions).some(
      option => selectedOptions[option] === '-'
    ) || email === ''
  return (
    <div className={styles.modalFormWrapper__FormPanel}>
      <div className={styles.modalFormWrapper__FormPanel__Container}>
        <div className={styles.modalFormWrapper__FormPanel__Header}>
          <Title
            classes="title--secondary"
            type="h3"
            text="Select your preferred size"
          />
          <p>We'll email you when is back in stock</p>
        </div>
        <form
          onSubmit={e => handleOnSubmit(e)}
          noValidate
          className={styles.modalFormWrapper__Form}
        >
          <BackInStockForm
            selectedOptions={selectedOptions}
            availableOptions={availableOptions}
            onSelectFormOptions={handleSelectingFormOptions}
          />
          <div className={styles.modalFormWrapper__Form__Footer}>
            <div className={styles.modalFormWrapper__Form__Email}>
              <EmailFieldValidation
                setInputValue={setEmail}
                placeholder={Translations.localise(
                  'product.oos-modal.form.email-placeholder'
                )}
              />
            </div>

            <div className={styles.modalFormWrapper__Form__Notification}>
              <FormNotification state={showFormState} />
            </div>

            {showFormState === 'success' ? (
              <Button
                variant="primary"
                fullWidth="always"
                onClick={handleCloseModal}
              >
                {Translations.localise('product.oos-modal.form.close-button')}
              </Button>
            ) : (
              <SubmitButton
                fullWidth="always"
                type="submit"
                state={buttonState}
                disabled={isSubmitButtonDisabled}
              >
                {Translations.localise('product.oos-modal.form.button')}
              </SubmitButton>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default BackInStockFormWrapper
